import { useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown, Wrench, ArrowUpRight, Search, X, Plus, Check } from "lucide-react";
import { toast } from "sonner";
import { Reveal, SectionLabel } from "@/components/Reveal";
import { SERVICE_ICONS as ICONS } from "@/lib/icons";
import { norm, stems, hitsQuery } from "@/lib/search";

const grouped = (rows = []) => {
  const out = [];
  rows.forEach((r) => {
    const key = r.group || "";
    const last = out[out.length - 1];
    if (last && last.group === key) last.items.push(r);
    else out.push({ group: key, items: [r] });
  });
  return out;
};

const PriceRow = ({ row, chosen, onAdd }) => (
  <div className="flex items-baseline gap-3 py-2.5" data-testid="price-row">
    <span className="text-sm text-slate-300 sm:text-[15px]">{row.label}</span>
    <span className="mb-1 h-px flex-1 bg-gradient-to-r from-white/15 to-white/5" />
    <span className="whitespace-nowrap font-head text-base font-bold tracking-tight text-white sm:text-lg">
      {row.price}
    </span>
    <button
      type="button"
      onClick={onAdd}
      title={chosen ? "Jau pieteikumā" : "Pievienot pieteikumam"}
      aria-label={chosen ? "Jau pieteikumā" : "Pievienot pieteikumam"}
      data-testid={`price-add-${row._key}`}
      className={`flex h-7 w-7 shrink-0 translate-y-1 items-center justify-center border transition-all duration-300 ${
        chosen
          ? "border-rose-500 bg-rose-600 text-white"
          : "border-white/15 text-slate-500 hover:border-rose-500 hover:bg-rose-600/15 hover:text-rose-300"
      }`}
    >
      {chosen ? <Check className="h-3.5 w-3.5" /> : <Plus className="h-3.5 w-3.5" />}
    </button>
  </div>
);

const Category = ({ cat, index, isOpen, onToggle, picked, onAdd }) => {
  const Icon = ICONS[cat.icon] || Wrench;
  const blocks = useMemo(() => grouped(cat.rows), [cat.rows]);

  return (
    <div
      className={`border-b border-white/10 transition-colors duration-500 ${isOpen ? "bg-white/[0.03]" : "hover:bg-white/[0.015]"}`}
      data-testid={`price-category-${cat.id}`}
    >
      <button
        onClick={onToggle}
        data-testid={`price-category-toggle-${cat.id}`}
        className="flex w-full items-center gap-4 px-5 py-6 text-left sm:gap-6 sm:px-8"
      >
        <span className={`font-mono-t text-[10px] tracking-[0.2em] ${isOpen ? "text-rose-400" : "text-slate-600"}`}>
          0{index + 1}
        </span>
        <Icon
          className={`h-7 w-7 shrink-0 transition-all duration-500 ${isOpen ? "scale-110 text-rose-500" : "text-slate-500"}`}
          strokeWidth={1.4}
        />
        <span className="min-w-0 flex-1">
          <span className="block font-head text-xl font-bold uppercase leading-tight tracking-tight text-slate-100 sm:text-2xl">
            {cat.title}
          </span>
          <span className="mt-1 block font-mono-t text-[9px] uppercase tracking-[0.2em] text-slate-600">
            {cat.rows?.length || 0} pozīcijas
          </span>
        </span>
        <span
          className={`flex h-9 w-9 shrink-0 items-center justify-center border transition-all duration-500 ${
            isOpen ? "border-rose-500 bg-rose-600/15 text-rose-400" : "border-white/15 text-slate-500"
          }`}
        >
          <ChevronDown className={`h-4 w-4 transition-transform duration-500 ${isOpen ? "rotate-180" : ""}`} />
        </span>
      </button>

      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div
            key="body"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden"
          >
            <div className="px-5 pb-8 sm:px-8 sm:pl-[104px]">
              {blocks.map((b, bi) => (
                <div key={`${b.group}-${bi}`} className={bi ? "mt-6" : ""}>
                  {b.group && (
                    <div className="flex items-center gap-3 border-l-2 border-rose-600 pl-3">
                      <span className="font-head text-sm font-bold uppercase tracking-tight text-rose-300">
                        {b.group}
                      </span>
                    </div>
                  )}
                  <div className={b.group ? "mt-1 pl-3" : ""}>
                    {b.items.map((r, ri) => (
                      <PriceRow
                        key={`${r.label}-${ri}`}
                        row={r}
                        chosen={picked.some((p) => p.key === r._key)}
                        onAdd={() =>
                          onAdd({
                            key: r._key,
                            label: r.group ? `${r.group} — ${r.label}` : r.label,
                            price: r.price,
                            category: cat.title,
                          })
                        }
                      />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export const PriceList = ({ categories = [], picked = [], onAdd }) => {
  const [open, setOpen] = useState(0);
  const [q, setQ] = useState("");

  const keyed = useMemo(
    () => categories.map((c) => ({ ...c, rows: (c.rows || []).map((r, i) => ({ ...r, _key: `${c.id}-${i}` })) })),
    [categories]
  );

  const query = norm(q.trim());
  const tokens = useMemo(() => stems(q.trim()), [q]);
  const filtered = useMemo(() => {
    if (!query) return keyed;
    return keyed
      .map((c) => {
        const catHit = hitsQuery(c.title, tokens);
        const rows = c.rows.filter((r) => catHit || hitsQuery(`${r.label} ${r.group}`, tokens));
        return { ...c, rows };
      })
      .filter((c) => c.rows.length > 0);
  }, [keyed, query, tokens]);

  const addRow = (row) => {
    onAdd?.(row);
    toast.success(`Pievienots pieteikumam: ${row.label}`);
  };

  const hits = filtered.reduce((n, c) => n + (c.rows?.length || 0), 0);

  return (
    <section id="pakalpojumi" className="relative border-t border-white/10 py-24 sm:py-32">
      <div className="mx-auto max-w-[1500px] px-5 sm:px-10">
        <SectionLabel num="/ 02">Pakalpojumi & cenas</SectionLabel>
        <Reveal>
          <h2 className="mt-5 font-head text-4xl font-extrabold uppercase italic leading-[0.92] tracking-tight sm:text-5xl lg:text-6xl">
            <span className="metal-text">CENRĀDIS —</span> <span className="crimson-text">GODĪGI</span>
          </h2>
        </Reveal>
        <Reveal delay={0.08}>
          <p className="mt-5 max-w-2xl text-sm leading-relaxed text-slate-400 md:text-base">
            Zemāk norādītās cenas ir darba izmaksas bez detaļām. Precīzu summu apstiprinām pēc automašīnas apskates —
            bez pārsteigumiem rēķinā.
          </p>
        </Reveal>

        <Reveal delay={0.1}>
          <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center">
            <div className="relative flex-1">
              <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Meklē pakalpojumu — piem. bremzes, eļļa, amortizators"
                data-testid="price-search"
                className="w-full border border-white/12 bg-black/50 py-3.5 pl-11 pr-11 text-sm text-slate-100 outline-none transition-colors placeholder:text-slate-600 focus:border-rose-500"
              />
              {q && (
                <button
                  onClick={() => setQ("")}
                  data-testid="price-search-clear"
                  aria-label="Notīrīt meklēšanu"
                  className="absolute right-3 top-1/2 -translate-y-1/2 border border-white/10 p-1.5 text-slate-400 transition-colors hover:border-rose-500 hover:text-rose-400"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
            {query && (
              <span
                data-testid="price-search-count"
                className="font-mono-t text-[10px] uppercase tracking-[0.2em] text-rose-400 sm:w-40 sm:text-right"
              >
                {hits} rezultāt{hits === 1 ? "s" : "i"}
              </span>
            )}
          </div>
        </Reveal>

        <Reveal delay={0.12}>
          <div className="mt-6 border border-white/10 bg-[#0c0c11]" data-testid="price-list">
            {filtered.map((cat, i) => (
              <Category
                key={cat.id}
                cat={cat}
                index={categories.findIndex((c) => c.id === cat.id)}
                isOpen={query ? true : open === i}
                onToggle={() => !query && setOpen(open === i ? -1 : i)}
                picked={picked}
                onAdd={addRow}
              />
            ))}
            {filtered.length === 0 && (
              <p className="px-8 py-10 font-mono-t text-xs uppercase tracking-widest text-slate-500" data-testid="price-empty">
                {query ? `Nekas netika atrasts: "${q}"` : "Cenrādis tiek atjaunots."}
              </p>
            )}
          </div>
        </Reveal>

        <Reveal delay={0.16}>
          <div className="mt-12 flex flex-col items-center gap-4 border border-rose-600/30 bg-rose-600/[0.06] px-6 py-10 text-center">
            <span className="font-mono-t text-[10px] uppercase tracking-[0.3em] text-rose-400">
              Neatradi savu darbu?
            </span>
            <span className="font-head text-2xl font-extrabold uppercase italic leading-tight tracking-tight text-white sm:text-3xl">
              Sazinies un saņem precīzu piedāvājumu
            </span>
            <a
              href="#pieteikums"
              data-testid="pricelist-cta"
              className="group mt-2 inline-flex items-center gap-3 border border-rose-500 bg-rose-600 px-8 py-4 font-head text-base font-bold uppercase tracking-tight text-white transition-all duration-300 hover:bg-rose-500 hover:shadow-[0_0_40px_-8px_rgba(225,29,72,0.8)]"
            >
              Pieteikties servisam
              <ArrowUpRight className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
            </a>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
